import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './stats.css';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';

const Stats = () => {
  const [userdata, setUserdata] = useState(null);
  const [starComment, setStarComment] = useState('');
  const [starCount, setStarCount] = useState(0);
  const [error, setError] = useState(null);

  const colors = [
    'url(#grad1)',
    'url(#grad2)',
    'url(#grad3)',
    'url(#grad4)',
    'url(#grad5)',
    'url(#grad6)'
  ];

  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const authorizedUsers = Object.freeze([
    '108129870778035595919',
    '114439477008986381098',
    '102906129011434823565',
    '108247700055597509497'
  ]);

  const getUser = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/ttc/login/userdata`, { withCredentials: true, timeout: 8000 });
      if (response?.data?.user) {
        const user = {
          RF: Array.isArray(response.data.user.RF) ? response.data.user.RF : [],
          RR: Array.isArray(response.data.user.RR) ? response.data.user.RR : [],
          FR: Array.isArray(response.data.user.FR) ? response.data.user.FR : [],
          FF: Array.isArray(response.data.user.FF) ? response.data.user.FF : [],
          displayName: response.data.user.displayName || '',
          recipe_evaluated: Array.isArray(response.data.user.recipe_evaluated) ? response.data.user.recipe_evaluated : [],
          googleId: response.data.user.googleId || '',
          picture: response.data.user.picture || 'profile.png'
        };
        setUserdata(user);
      } else {
        setError('User data not found. Please login again.');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to fetch user data. Please try again later.');
      setUserdata({});
    }
  }, [BACKEND_URL]);

  const calculateStars = useCallback(() => {
    if (!userdata) return;
    const total =
      (userdata.RF?.length || 0) +
      (userdata.RR?.length || 0) +
      (userdata.FF?.length || 0) +
      (userdata.FR?.length || 0);

    if (total >= 300) {
      setStarComment('You are a 5 star rated user. Keep it up!');
      setStarCount(5);
    } else if (total >= 100) {
      setStarComment('You are a 4 star rated user. Keep it up!');
      setStarCount(4);
    } else if (total >= 60) {
      setStarComment('You are a 3 star rated user. Keep it up!');
      setStarCount(3);
    } else if (total >= 30) {
      setStarComment('You are a 2 star rated user. Keep it up!');
      setStarCount(2);
    } else if (total >= 10) {
      setStarComment('You are a 1 star rated user. Keep it up!');
      setStarCount(1);
    } else {
      const diff = 10 - total;
      setStarComment(`Evaluate ${diff} more recipes to get your first star!`);
      setStarCount(0);
    }
  }, [userdata]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    if (userdata?.recipe_evaluated) {
      calculateStars();
    }
  }, [userdata, calculateStars]);

  if (error) return <div>{error}</div>;
  if (!userdata) return <div>Loading...</div>;

  const data = [
    { name: 'Real', value: (userdata.RF?.length || 0) + (userdata.RR?.length || 0) },
    { name: 'Fake', value: (userdata.FF?.length || 0) + (userdata.FR?.length || 0) }
  ];

  const totalRecipes = userdata.recipe_evaluated?.length || 0;

  return (
    <div id="statbox">
      {authorizedUsers.includes(userdata.googleId) && (
        <button
          className="detailed-stats-btn"
          style={{
            position: 'relative',
            top: 'auto',
            left: 'auto',
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/stats')}
        >
          Get Detailed Stats
        </button>
      )}

      <div className="animate-gradient-text">
        <h1>
          <i>Hi {userdata.displayName.replace(/</g, '&lt;')}, here are your stats</i>
        </h1>
      </div>

      {totalRecipes > 0 ? (
        <div className="chart-container">
          <svg width={0} height={0}>
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#ff7e5f', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#feb47b', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#6a11cb', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#2575fc', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="grad3" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#f7f8f8', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#e9e9e9', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="grad4" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#00c6ff', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#0072ff', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="grad5" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#ff416c', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#ff4b2b', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="grad6" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#a8e063', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#56ab2f', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
          </svg>
          <PieChart width={600} height={400}>
            <Pie
              data={data}
              cx={300}
              cy={200}
              labelLine={false}
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-in-out"
              isAnimationActive={true}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [`${value} recipes`, '']} />
            <Legend layout="vertical" align="right" verticalAlign="middle" />
          </PieChart>
        </div>
      ) : (
        <div id="chartdiv" style={{ height: '0px' }}>No evaluations yet!</div>
      )}

      <h1 id="star_review">{starComment}</h1>
      <div className="rating-stars">
        {[...Array(starCount)].map((_, index) => (
          <span
            className="star-icon"
            key={index}
            style={{
              color: '#F7A115',
              fontSize: '60px'
            }}
          >
            ★
          </span>
        ))}
      </div>
    </div>
  );
};

export default Stats;
